import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Box,
    IconButton,
    CircularProgress,
    Divider,
    Alert
} from '@mui/material';
import { CloseOutlined, BlockOutlined, CheckCircleOutline, InfoOutlined, PersonOutline, EmailOutlined } from '@mui/icons-material';

import { deactivateEmployeeApi } from 'utils/API/Mis Apis/HrPortelApis';
import { globalNotifyError } from 'views/utilities/AlertToast';
import { handleCatchError } from 'utils/CatchHandler';

const ToggleEmployeeStatusModal = ({ open, onClose, account, targetStatus = 'inactive', onSuccess }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isDeactivating = targetStatus === 'inactive' || targetStatus === 'deactive';
    const employeeName = account?.employee_name || account?.name || account?.email?.split('@')[0] || 'Employee';
    const employeeEmail = account?.email || '-';

    const handleConfirm = async () => {
        const crossUserId = account?.cross_user_id || account?.crossbanking_id || account?.id;
        if (!crossUserId) {
            globalNotifyError('Unable to identify employee Crossbanking ID');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                id: crossUserId,
                status: isDeactivating ? 'inactive' : 'active'
            };

            const response = await deactivateEmployeeApi(payload);

            if (response?.data?.success) {
                onClose();
                if (onSuccess) onSuccess();
            } else if (response?.data?.errors?.id) {
            } else if (response?.data?.message) {
            } else {
                onClose();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error updating employee credit access status:', error);
            handleCatchError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !isSubmitting && onClose()}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1,
                    overflow: 'hidden'
                }
            }}
        >
            {/* Modal Header */}
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Stack direction="row" spacing={1.75} alignItems="center">
                    <Box
                        sx={{
                            p: 1.25,
                            borderRadius: 2.5,
                            bgcolor: isDeactivating ? '#fef2f2' : '#f0fdf4',
                            color: isDeactivating ? '#dc2626' : '#16a34a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${isDeactivating ? '#fee2e2' : '#bbf7d0'}`
                        }}
                    >
                        {isDeactivating ? <BlockOutlined sx={{ fontSize: 26 }} /> : <CheckCircleOutline sx={{ fontSize: 26 }} />}
                    </Box>
                    <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary', fontSize: '1.15rem' }}>
                            {isDeactivating ? 'Deactivate Employee Credit Access?' : 'Activate Employee Credit Access?'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {isDeactivating
                                ? 'Disable corporate credit limit and card spending'
                                : 'Restore corporate credit limit and spending permissions'}
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={onClose} disabled={isSubmitting} sx={{ color: 'text.secondary' }}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2, pb: 2 }}>
                <Stack spacing={2.5}>
                    {/* Modal Body Message */}
                    <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '0.95rem', lineHeight: 1.5 }}>
                        Are you sure you want to {isDeactivating ? 'deactivate' : 'activate'}{' '}
                        <Box component="span" fontWeight={700} sx={{ color: isDeactivating ? '#b91c1c' : '#15803d' }}>
                            {employeeName}
                        </Box>
                        ?
                    </Typography>

                    {/* Employee Mini Card */}
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        <Stack spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <PersonOutline sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ width: 60, fontWeight: 600 }}>
                                    Name:
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                    {employeeName}
                                </Typography>
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1}>
                                <EmailOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ width: 60, fontWeight: 600 }}>
                                    Email:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {employeeEmail}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>

                    {/* Note Box */}
                    {isDeactivating ? (
                        <Alert
                            severity="warning"
                            icon={<InfoOutlined fontSize="small" />}
                            sx={{
                                borderRadius: 2,
                                bgcolor: '#fffbeb',
                                border: '1px solid #fde68a',
                                color: '#92400e',
                                '& .MuiAlert-icon': { color: '#d97706' },
                                fontSize: '0.825rem',
                                lineHeight: 1.5
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: '#b45309', mb: 0.25 }}>
                                Note:
                            </Typography>
                            This action will only disable the employee&apos;s Corporate Credit Limit &amp; Wallet Payment access. It will
                            not affect their regular employee account or portal access.
                        </Alert>
                    ) : (
                        <Alert
                            severity="success"
                            icon={<CheckCircleOutline fontSize="small" />}
                            sx={{
                                borderRadius: 2,
                                bgcolor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                color: '#166534',
                                '& .MuiAlert-icon': { color: '#16a34a' },
                                fontSize: '0.825rem',
                                lineHeight: 1.5
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: '#15803d', mb: 0.25 }}>
                                Note:
                            </Typography>
                            This will restore the employee&apos;s Corporate Credit Limit &amp; Wallet Payment access.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <Divider />

            {/* Action Buttons */}
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                    onClick={onClose}
                    disabled={isSubmitting}
                    sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        fontWeight: 600,
                        px: 2
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={
                        isSubmitting ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : isDeactivating ? (
                            <BlockOutlined fontSize="small" />
                        ) : (
                            <CheckCircleOutline fontSize="small" />
                        )
                    }
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 2.5,
                        py: 0.85,
                        bgcolor: isDeactivating ? '#dc2626' : '#16a34a',
                        '&:hover': {
                            bgcolor: isDeactivating ? '#b91c1c' : '#15803d'
                        },
                        boxShadow: isDeactivating ? '0 4px 12px rgba(220, 38, 38, 0.25)' : '0 4px 12px rgba(22, 163, 74, 0.25)'
                    }}
                >
                    {isSubmitting
                        ? isDeactivating
                            ? 'Deactivating...'
                            : 'Activating...'
                        : isDeactivating
                        ? 'Yes, Deactivate Credit Access'
                        : 'Yes, Activate Credit Access'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ToggleEmployeeStatusModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    account: PropTypes.object,
    targetStatus: PropTypes.string,
    onSuccess: PropTypes.func
};

export default ToggleEmployeeStatusModal;
